// IndexedDB 数据库层：结构化数据（模型配置、剪辑任务、agent checkpoint）。
//
// store 划分：
//   settings    keyPath "key"   模型配置 { key:"llm", apiKey, baseURL, modelName }
//   projects    keyPath "id"    剪辑任务 { id:uuid, name, protocol, updatedAt, materials }
//   checkpoints keyPath "key"   agent checkpoint（由 IndexedDBSaver 读写）
//   writes      keyPath "key"   agent pending writes（由 IndexedDBSaver 读写）
//   downloads   keyPath "id"    已下载系统资源索引（文件字节在 OPFS）
//   clipboard   外部 key        跨集复制素材/片段的剪贴板（Blob + 片段数据）
//
// 与 opfs.js 的分工：二进制文件走 OPFS，结构化数据走这里。

const DB_NAME = 'web-site';
const DB_VERSION = 3;

let dbPromise = null;

export function openDb() {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains('settings')) db.createObjectStore('settings', { keyPath: 'key' });
            if (!db.objectStoreNames.contains('projects')) db.createObjectStore('projects', { keyPath: 'id' });
            // checkpoints/writes 用 out-of-line key（显式传入复合 key 字符串），避免 keyPath 与序列化 key 冲突。
            if (!db.objectStoreNames.contains('checkpoints')) db.createObjectStore('checkpoints');
            if (!db.objectStoreNames.contains('writes')) db.createObjectStore('writes');
            if (!db.objectStoreNames.contains('downloads')) db.createObjectStore('downloads', { keyPath: 'id' });
            // 剪贴板使用外部 key（固定写入 'current'），避免 Blob 与 keyPath 互相干扰。
            if (!db.objectStoreNames.contains('clipboard')) db.createObjectStore('clipboard');
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
    return dbPromise;
}

/** 包装单个 IDBRequest 为 Promise。 */
function requestResult(request) {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

/** 在单个 store 上开事务并执行 fn(store)，返回 fn 的结果。 */
async function withStore(storeName, mode, fn) {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(storeName, mode);
        const store = transaction.objectStore(storeName);
        let outcome;
        try {
            outcome = fn(store);
        } catch (error) {
            reject(error);
            return;
        }
        // fn 可能返回 IDBRequest（同步请求）或 Promise（如 cursor 遍历）。
        const settle = value => {
            transaction.oncomplete = () => resolve(value);
            transaction.onerror = () => reject(transaction.error);
            transaction.onabort = () => reject(transaction.error || new Error('IndexedDB transaction aborted'));
        };
        if (outcome && typeof outcome.then === 'function') {
            outcome.then(settle, reject);
        } else {
            settle(outcome);
        }
    });
}

export function get(storeName, key) {
    return withStore(storeName, 'readonly', store => requestResult(store.get(key)));
}

export function put(storeName, value, key) {
    return withStore(storeName, 'readwrite', store => requestResult(key === undefined ? store.put(value) : store.put(value, key)));
}

export function del(storeName, key) {
    return withStore(storeName, 'readwrite', store => requestResult(store.delete(key)));
}

export function getAll(storeName) {
    return withStore(storeName, 'readonly', store => requestResult(store.getAll()));
}

export function clear(storeName) {
    return withStore(storeName, 'readwrite', store => requestResult(store.clear()));
}

/**
 * 用游标遍历一个 store 的所有记录。fn(key, value) 每条调用一次；
 * fn 返回 false 可提前终止遍历。返回 Promise，遍历结束后 resolve。
 */
export function each(storeName, fn) {
    return withStore(storeName, 'readonly', store => new Promise((resolve, reject) => {
        const cursorRequest = store.openCursor();
        cursorRequest.onsuccess = () => {
            const cursor = cursorRequest.result;
            if (!cursor) { resolve(); return; }
            const stop = fn(cursor.key, cursor.value);
            if (stop === false) { resolve(); return; }
            cursor.continue();
        };
        cursorRequest.onerror = () => reject(cursorRequest.error);
    }));
}

export default { openDb, get, put, del, getAll, each, clear };
